/**
 * LibraryMenusModal - "Elige tu menú": UNA sola lista con todo lo que se puede elegir.
 *
 * Hasta el 13-08-2026 esto eran dos pestañas, Biblioteca y Recetario, y Francisco pidió
 * juntarlas: «unifica el recetario con la biblioteca, que ya no estén separados». Para
 * quien elige la comida son lo mismo -- menús que puede meterse en el plato --, y la
 * diferencia era nuestra, no suya:
 *
 * 1. Recetas del recetario ELM (`menu_templates`): no traen cantidades cerradas, así que
 *    al elegir una el motor la cuadra a tus macros (POST /calculator/menu-apply). Traen
 *    foto y nombre de plato.
 * 2. Menús reales (`meal_library`, 266k comidas de clientes ya cuadradas con el método):
 *    vienen con las cantidades puestas y se vuelcan tal cual. Filosofía (nota 2026-07-16):
 *    cercanía, no exactitud.
 *
 * Esa diferencia sigue existiendo por dentro -- cada tarjeta hace lo suyo al pulsarla --,
 * pero ya no se le pide al cliente que elija primero de qué cajón quiere sacar la comida.
 *
 * EL ORDEN: NI UNAS NI OTROS PRIMERO (Francisco, 19-08: «siempre salen primero las recetas
 * y no quiero eso»). Hasta hoy se pintaban las 159 recetas y detrás los menús reales, así
 * que para ver un menú de la gente había que bajar toda la lista. Ahora las dos poblaciones
 * se van alternando en proporción a lo que hay de cada una -- si hay 20 recetas y 100 menús,
 * sale una receta cada cinco menús --, cada una en su propio orden: los menús reales por
 * cercanía a tu objetivo (eso lo hace el servidor) y las recetas como vienen del catálogo.
 *
 * No se mezclan ordenando los dos juntos por lo bien que encajan, que habría sido lo ideal:
 * se midió y cuadrar las 159 recetas contra un objetivo cuesta 52 s, así que no se puede
 * hacer mientras el cliente espera. Alternar no inventa una precisión que no tenemos.
 *
 * Y SE PUEDE FILTRAR, que no es lo mismo que separar: «Recetas» es un chip más, al lado de
 * los momentos del día, y se queda solo con ellas igual que «Cenas» se queda con las cenas.
 * Por defecto no está puesto y sale todo junto.
 *
 * Y LA CARGA VA EN PARALELO. Antes la biblioteca solo se pedía al entrar en su pestaña y
 * podía tardar más de 30 segundos (Jesús se quedó mirándola). Ahora se pide al abrir, a la
 * vez que el recetario: la lista se puede usar desde el primer segundo con las recetas, y
 * los menús reales se suman abajo cuando llegan.
 *
 * EL REDISEÑO «ELIGE TU MENÚ» (handoff del 16-10, docs/diseño/design_handoff_elige_tu_menu).
 * Una hoja que sube desde abajo en el teléfono (en el ordenador, la ventana de siempre):
 *   - siete filtros EXCLUYENTES en una fila que se desliza, con flechas si no caben y con
 *     cuántas opciones deja cada uno (con la búsqueda, el margen y el origen ya aplicados);
 *   - el buscador y, detrás del botón «±N g», el margen (1-15 g) y el origen;
 *   - UNA tarjeta para menús y recetas: arriba qué es y de quién, el título, los tres macros
 *     (en los menús, con lo que se desvían del objetivo), tres ingredientes y «Ver más», y
 *     el botón de elegir. El menú se vuelca tal cual; la receta se cuadra al elegirla.
 *
 * Los menús de la gente los sigue filtrando el SERVIDOR por momento (ver el 29-08 abajo):
 * no es la misma población pedir «Cenas» que pedir «Todas». Por eso cada filtro se pide
 * una vez y se guarda; el número de los filtros que aún no han llegado no se pinta (no se
 * inventa) y se van pidiendo detrás, uno a uno, sin parar la lista.
 *
 * Fuera del rediseño se quedan el orden («Las más usadas» es el de siempre) y el conmutador
 * Método/Reales del ordenador: el handoff no los trae.
 */
import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Search, X, SlidersHorizontal, Check, RefreshCw } from 'lucide-react';
import { BIBLIOTECA_DE_CLIENTES } from '../../lib/menuFuentes';
import { CHIP_ENTRENADORES, etiquetaAutor, soloDeEntrenadores } from '../../lib/menusDeEntrenadores';
import { num1 } from '../../lib/numeros';
import FilaConFlechas from '../FilaConFlechas';
import { algoCon } from '../../lib/loQueFaltaParaCuadrar';

const normalizar = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const MACRO_NOMBRE = {
    P: 'proteína',
    H: 'hidratos',
    G: 'grasa',
};

// A partir de esta desviación (g por macro) la receta NO se vuelca sin que el usuario
// lo vea: se le enseña de cuánto se pasa y decide. Mismo umbral laxo que usa el motor
// (MARGEN_MENU_RELAX en backend/meal_templates.py). El recetario va en best_effort, o sea
// que el backend nunca rechaza la receta elegida; este es el único freno que hay.
const MARGEN_AVISO = 12;

// Macros que se salen del margen al cuadrar la receta, con su diferencia en gramos
// (positiva = te pasas, negativa = te falta).
const desviosFuera = (totales, objetivo) => ['P', 'H', 'G']
    .map(m => ({ m, diff: Math.round(((totales?.[m] || 0) - (objetivo?.[m] || 0)) * 10) / 10 }))
    .filter(d => Math.abs(d.diff) > MARGEN_AVISO);

// Los siete filtros del handoff, en su orden. Los cuatro momentos son los del recetario
// (`momento` de menu_templates) y los de la biblioteca (`momentos`, _momentos_biblioteca.py).
const FILTROS = [
    ['todos', 'Todas'],
    ['recetas', 'Recetas'],
    [CHIP_ENTRENADORES, 'De entrenadores'],
    ['comida', 'Comidas'],
    ['cena', 'Cenas'],
    ['desayuno', 'Desayunos'],
    ['merienda', 'Meriendas'],
];
const ES_MOMENTO = { desayuno: true, comida: true, merienda: true, cena: true };
const MOMENTO_MAYUS = { desayuno: 'DESAYUNO', comida: 'COMIDA', merienda: 'MERIENDA', cena: 'CENA' };

// Lo que se le pide al servidor para cada filtro. «Recetas» no pide menús.
const cuerpoDelFiltro = (filtro) => (ES_MOMENTO[filtro] ? { momento: filtro }
    : filtro === CHIP_ENTRENADORES ? { solo_entrenadores: true } : {});

// EL TÍTULO DE UN MENÚ (handoff: «los menús de usuario también llevan nombre; ya no se usan
// los macros como título»). Casi ninguno trae `nombre`: se compone con sus tres primeros
// alimentos, «Pavo, atún y pan de fibra».
const tituloDelMenu = (menu) => {
    if (menu.nombre) return menu.nombre;
    if (menu.de_jesus && menu.menu_elm?.nombre) return menu.menu_elm.nombre;
    const nombres = (menu.items || []).slice(0, 3).map((it, i) => {
        const n = (it.nombre || '').replace(/\s*\(.*?\)\s*/g, ' ').trim();
        return i === 0 ? n : n.charAt(0).toLowerCase() + n.slice(1);
    }).filter(Boolean);
    if (nombres.length <= 1) return nombres[0] || 'Menú';
    return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
};

const conSigno = (d) => `${d > 0 ? '+' : '−'}${num1(Math.abs(d))}`;

// ── La tarjeta, la misma para menús y recetas ───────────────────────────────────────
const TarjetaMenu = ({
    kicker, origen, origenNaranja, titulo, macros, esMenu, ingredientes, social,
    textoBoton, elegido, ocupado, deshabilitado, onElegir, testId, estado,
}) => {
    const [abierta, setAbierta] = React.useState(false);
    const visibles = abierta ? ingredientes : ingredientes.slice(0, 3);
    const quedan = ingredientes.length - 3;
    return (
        <article className="rounded-2xl bg-[#1f1f1f] border border-[#2a2a2a] p-3.5 flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-[11px] font-bold tracking-[0.12em] text-[#8a8580] uppercase truncate">{kicker}</span>
                <span className={`text-[11px] font-semibold flex-shrink-0 max-w-[50%] truncate ${origenNaranja ? 'text-brand' : 'text-[#8a8580]'}`}
                    data-testid="menu-autor">{origen}</span>
            </div>
            <h3 className="font-heading text-[21px] font-bold leading-[1.1] text-foreground [text-wrap:pretty]">{titulo}</h3>
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5 items-stretch">
                {/* La receta no tiene cifras hasta que se ajusta (MAC-02, 14-10): sin ellas. */}
                {!macros && (
                    <span className="col-span-3 text-xs text-[#8a8580] self-center">Las cantidades se ajustan a ti al elegirla</span>
                )}
                {(macros || []).map(({ k, v, d }) => (
                    <div key={k} className="rounded-lg bg-[#171717] px-2 py-1.5 flex items-baseline gap-1 min-w-0">
                        <span className="text-[10px] font-bold text-[#8a8580]">{k}</span>
                        <span className="font-heading text-[17px] font-bold text-foreground leading-none">{num1(v)}</span>
                        {esMenu && d !== null && Math.abs(d) >= 0.5 && (
                            <span className={`text-[10px] font-semibold ${Math.abs(d) > 2 ? 'text-[#e8a23a]' : 'text-[#6f6a65]'}`}>{conSigno(d)}</span>
                        )}
                    </div>
                ))}
                {/* Lo que dice el servidor (`clavado`, `cuadrada`), no «Cuadra» en todos (16-10). */}
                {esMenu ? (estado && (
                    <span className="rounded-lg bg-[#13261b] text-[#4fd08a] text-[11px] font-bold px-2 flex items-center gap-1 whitespace-nowrap"
                        data-testid="menu-estado">
                        <Check className="w-3 h-3" strokeWidth={3} /> {estado}
                    </span>
                )) : (
                    <span className="rounded-lg bg-[#2a1b10] text-[#ff8a4d] text-[11px] font-bold px-2 flex items-center gap-1 whitespace-nowrap">
                        <RefreshCw className="w-3 h-3" strokeWidth={2.5} /> Se ajusta
                    </span>
                )}
            </div>
            {/* Las recetas no traen cantidades (solo un punto): la columna de «120 g» se estrecha. */}
            <div className={`grid ${ingredientes.some((g) => g.q !== '·') ? 'grid-cols-[52px_1fr]' : 'grid-cols-[10px_1fr]'} gap-x-2 gap-y-1 text-sm leading-[1.35]`}>
                {visibles.map((g, i) => (
                    <React.Fragment key={i}>
                        <span className="font-bold text-brand whitespace-nowrap overflow-hidden text-ellipsis">{g.q}</span>
                        <span className="text-[#d9d5d0] min-w-0">{g.n}</span>
                    </React.Fragment>
                ))}
                {quedan > 0 && (
                    <button type="button" onClick={() => setAbierta((a) => !a)}
                        className="col-start-2 justify-self-start text-[13px] font-semibold text-[#8a8580] hover:text-foreground mt-0.5">
                        {abierta ? 'Ver menos' : `+${quedan} ${quedan === 1 ? 'ingrediente' : 'ingredientes'} más`}
                    </button>
                )}
            </div>
            <div className="border-t border-[#2a2a2a] pt-2.5 flex items-center justify-between gap-3">
                <span className="text-xs text-[#8a8580] min-w-0">{social}</span>
                <button type="button" onClick={onElegir} disabled={deshabilitado} data-testid={testId}
                    className={`h-8 px-4 rounded-full text-[13px] font-bold flex items-center gap-1.5 flex-shrink-0 transition-colors disabled:opacity-60 ${
                        elegido ? 'bg-brand text-[#140a04]' : 'bg-[#2c2c2c] text-foreground hover:bg-[#383838]'}`}>
                    {ocupado && <span className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent" />}
                    {elegido && !ocupado && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                    {elegido ? 'Elegido' : textoBoton}
                </button>
            </div>
        </article>
    );
};

const LibraryMenusModal = ({ open, mealKey, onClose, mealInfo, target, api, dayConfig, onApply }) => {
    // El filtro, uno solo cada vez (handoff: excluyentes). Por defecto «Todas».
    const [filtro, setFiltro] = React.useState('todos');
    // El margen que se ve en el deslizador y el que se pide: se pide al soltar un momento,
    // no con cada paso del dedo.
    const [margenVisto, setMargenVisto] = React.useState(5);
    const [margen, setMargen] = React.useState(5);
    const [opcionesAbiertas, setOpcionesAbiertas] = React.useState(false);
    // La procedencia, a la vista y con puerta (doc 57, F7; decision de Francisco 21-08:
    // opcion 3, «separar y etiquetar»). Ahora es el «Origen» de las opciones: «Todos» o
    // «Solo recetario». La eleccion se recuerda.
    const [verDeOtros, setVerDeOtros] = React.useState(() => {
        try { return localStorage.getItem('menus_ver_de_otros') !== 'no'; } catch { return true; }
    });
    const cambiarVerDeOtros = (v) => {
        setVerDeOtros(v);
        try { localStorage.setItem('menus_ver_de_otros', v ? 'si' : 'no'); } catch { /* privado */ }
    };
    const [textFilter, setTextFilter] = React.useState('');
    // Los menús de la gente, por filtro y margen: `${filtro}|${margen}` -> respuesta.
    const [cache, setCache] = React.useState({});
    const [cargando, setCargando] = React.useState({});
    const [error, setError] = React.useState(null);
    const [applying, setApplying] = React.useState(false);
    const [elegidoId, setElegidoId] = React.useState(null);

    const [recetario, setRecetario] = React.useState(null);
    const [recetarioLoading, setRecetarioLoading] = React.useState(false);
    const [recetarioError, setRecetarioError] = React.useState(null);
    const [aplicandoId, setAplicandoId] = React.useState(null);
    // Receta ya cuadrada pero lejos del objetivo, esperando el OK del usuario
    const [confirmacion, setConfirmacion] = React.useState(null);

    React.useEffect(() => {
        if (open) {
            setTextFilter('');
            setError(null);
            setRecetarioError(null);
            setFiltro('todos');
            setConfirmacion(null);
            setOpcionesAbiertas(false);
            setElegidoId(null);
            setCache({});
            setCargando({});
        }
    }, [open, mealKey]);

    React.useEffect(() => {
        const t = setTimeout(() => setMargen(margenVisto), 350);
        return () => clearTimeout(t);
    }, [margenVisto]);

    const clave = (f) => `${f}|${margen}`;

    const pedirMenus = React.useCallback(async (f, m) => {
        const res = await api('/api/calculator/library-menus', {
            method: 'POST',
            body: JSON.stringify({
                mealKey,
                // El objetivo lo define la calculadora; si aún no hay reparto
                // (0/0/0), el backend reparte el día con la config actual.
                macros_objetivo: target ? { P: target.P, H: target.H, G: target.G } : {},
                margen: m,
                // «LAS MÁS USADAS», el orden por defecto (artefacto «Compartir los menús»,
                // 24-09: «ese botón ya es el ranking de menús»).
                orden: 'usado',
                limit: 120,
                // EL FILTRO TAMBIÉN ACOTA LOS MENÚS DE LA GENTE (Francisco, 29-08): se pide
                // por `momentos`, no por la posición de la comida, que no dice el momento.
                ...cuerpoDelFiltro(f),
                ...(dayConfig || {}),
            }),
        });
        return {
            menus: res.menus || [],
            objetivo: res.objetivo || null,
            // «La base no está cosechada» no es «no hay ninguno que te cuadre» (punto 10.3).
            sinCosechar: !!res.filtros?.sin_cosechar,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mealKey, target?.P, target?.H, target?.G, JSON.stringify(dayConfig || {})]);

    // Primero el filtro puesto; luego, de uno en uno, los demás (para sus números).
    React.useEffect(() => {
        if (!open || !mealKey || !BIBLIOTECA_DE_CLIENTES) return undefined;
        let cancelado = false;
        const orden = [filtro, ...FILTROS.map(([k]) => k).filter((k) => k !== filtro && k !== 'recetas')]
            .filter((k) => k !== 'recetas');
        (async () => {
            for (const f of orden) {
                if (cancelado) return;
                const k = `${f}|${margen}`;
                if (cache[k]) continue;
                setCargando((c) => ({ ...c, [k]: true }));
                try {
                    const r = await pedirMenus(f, margen);
                    if (cancelado) return;
                    setCache((c) => ({ ...c, [k]: r }));
                } catch (err) {
                    if (cancelado) return;
                    if (f === filtro) setError('No se pudieron cargar los menús de la biblioteca.');
                    setCache((c) => ({ ...c, [k]: { menus: [], objetivo: null, fallo: true } }));
                } finally {
                    if (!cancelado) setCargando((c) => { const n = { ...c }; delete n[k]; return n; });
                }
            }
        })();
        return () => { cancelado = true; };
        // `cache` fuera a propósito: cada respuesta lo cambia y relanzaría la cola entera.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mealKey, filtro, margen, pedirMenus]);

    // El catálogo del recetario no depende de la comida ni del objetivo, pero sí de lo que
    // el cliente no quiere comer (29-10): se pide cada vez que se abre, porque entre una y
    // otra puede haber cambiado sus preferencias. Mientras llega, se ve el de antes.
    React.useEffect(() => {
        if (!open) return;
        let cancelado = false;
        const cargar = async () => {
            setRecetarioLoading(true);
            setRecetarioError(null);
            try {
                const res = await api('/api/calculator/menu-catalog');
                if (cancelado) return;
                setRecetario(res.menus || []);
            } catch (err) {
                if (!cancelado) setRecetarioError('No se pudo cargar el recetario.');
            }
            if (!cancelado) setRecetarioLoading(false);
        };
        cargar();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const actual = cache[clave(filtro)];
    const obj = actual?.objetivo || target || { P: 0, H: 0, G: 0 };
    const q = normalizar(textFilter.trim());

    // ── El filtrado del handoff: búsqueda + origen (+ el margen, que lo aplica el servidor)
    // y luego el filtro. Las recetas siempre pasan el margen: se escalan al elegirlas.
    const recetasDe = React.useCallback((f) => {
        if (f === CHIP_ENTRENADORES) return [];   // las del recetario no son «de entrenadores»
        return (recetario || []).filter((r) => {
            if (ES_MOMENTO[f] && !(r.momentos || []).includes(f)) return false;
            return !q || normalizar(r.nombre).includes(q) || (r.alimentos || []).some((a) => normalizar(a).includes(q));
        });
    }, [recetario, q]);
    // `null`: ese filtro aún no ha llegado.
    const menusDe = React.useCallback((f) => {
        if (f === 'recetas') return [];
        // «Solo recetario»: fuera lo que monta la gente. Menos con «De entrenadores» puesto:
        // esos los ha pedido él con el chip (como antes del 16-10).
        if (!verDeOtros && f !== CHIP_ENTRENADORES) return [];
        const r = cache[`${f}|${margen}`];
        if (!r) return null;
        const lista = f === CHIP_ENTRENADORES ? soloDeEntrenadores(r.menus) : r.menus;
        return lista.filter((m) => !q || normalizar(tituloDelMenu(m)).includes(q)
            || (m.items || []).some((it) => normalizar(it.nombre).includes(q)));
    }, [cache, margen, verDeOtros, q]);

    const recetasFiltradas = React.useMemo(() => recetasDe(filtro), [recetasDe, filtro]);
    const menusFiltrados = React.useMemo(() => menusDe(filtro) || [], [menusDe, filtro]);
    const buscandoMenus = !!cargando[clave(filtro)];

    // LA LISTA ÚNICA, ALTERNANDO (Francisco, 19-08: «siempre salen primero las recetas y no
    // quiero eso»). En cada paso entra la que va más atrasada respecto a lo que le toca; en
    // el empate, el menú (doc 57, F8: abrir con una tarjeta que ya trae los gramos).
    const mostrados = React.useMemo(() => {
        const recetas = recetasFiltradas.map((dato, i) => ({ tipo: 'receta', dato, i }));
        const reales = menusFiltrados.map((dato, i) => ({ tipo: 'menu', dato, i }));
        const salida = [];
        let a = 0, b = 0;
        while (a < recetas.length || b < reales.length) {
            const avanceRecetas = recetas.length ? a / recetas.length : 1;
            const avanceReales = reales.length ? b / reales.length : 1;
            if (b >= reales.length || (a < recetas.length && avanceRecetas < avanceReales)) {
                salida.push(recetas[a++]);
            } else {
                salida.push(reales[b++]);
            }
        }
        return salida;
    }, [recetasFiltradas, menusFiltrados]);

    const cuantos = (f) => {
        const m = menusDe(f);
        if (m === null) return null;
        return recetasDe(f).length + m.length;
    };

    const aplicar = async (menu) => {
        if (applying) return;
        setApplying(true);
        setElegidoId(`m-${menu.biblioteca_id}`);
        try { await onApply(menu); } catch { setElegidoId(null); } finally { setApplying(false); }
    };

    // Recetario: la receta no trae cantidades cerradas, las cuadra el backend a tus
    // macros al elegirla; lo que devuelve ya viene listo para volcar en la comida.
    // Si aun cuadrada se queda a más de MARGEN_AVISO de tu objetivo, NO se vuelca:
    // primero se enseña de cuánto se pasa y el usuario decide (vídeo de Jesús 04-08:
    // "para mí es poco visual, yo no me di cuenta y me preparo esta receta").
    const aplicarReceta = async (receta) => {
        if (aplicandoId) return;
        setAplicandoId(receta.id);
        setElegidoId(`r-${receta.id}`);
        setRecetarioError(null);
        try {
            const res = await api('/api/calculator/menu-apply', {
                method: 'POST',
                body: JSON.stringify({
                    plantilla_id: receta.id,
                    mealKey,
                    macros_objetivo: (obj.P || obj.H || obj.G) ? { P: obj.P, H: obj.H, G: obj.G } : {},
                    ...(dayConfig || {}),
                }),
            });
            const menu = { ...res, nombre: res.nombre || receta.nombre, origen: 'recetario' };
            const fuera = desviosFuera(res.macros_totales, res.macros_objetivo || obj);
            if (fuera.length) {
                setConfirmacion({ menu, fuera });
                return;
            }
            await onApply(menu);
        } catch (err) {
            setElegidoId(null);
            setRecetarioError('No se pudo crear esa receta con tus macros. Prueba con otra.');
        } finally {
            setAplicandoId(null);
        }
    };

    const confirmarReceta = async () => {
        if (!confirmacion) return;
        const { menu } = confirmacion;
        setConfirmacion(null);
        await onApply(menu);
    };

    // Arrastrar el asa hacia abajo cierra la hoja (handoff).
    const arrastre = React.useRef(null);
    const asa = {
        onTouchStart: (e) => { arrastre.current = e.touches[0].clientY; },
        onTouchEnd: (e) => {
            const desde = arrastre.current;
            arrastre.current = null;
            if (desde !== null && e.changedTouches[0].clientY - desde > 70) onClose();
        },
    };

    const nombreHueco = (mealKey && (mealInfo?.[mealKey]?.name || mealKey)) || '';
    const nRecetas = recetasFiltradas.length;
    const nMenus = menusFiltrados.length;
    const error_ = error || recetarioError;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="p-0 gap-0 overflow-hidden flex flex-col bg-[#161616] border-0 border-t border-[#262626] shadow-[0_-12px_40px_rgba(0,0,0,0.5)]
                    max-lg:left-0 max-lg:right-0 max-lg:top-14 max-lg:bottom-0 max-lg:translate-x-0 max-lg:translate-y-0 max-lg:max-w-none max-lg:max-h-none max-lg:rounded-t-[22px] max-lg:rounded-b-none
                    max-lg:data-[state=open]:slide-in-from-left-0 max-lg:data-[state=open]:slide-in-from-top-[60%] max-lg:data-[state=open]:zoom-in-100
                    lg:max-w-lg lg:h-[88dvh] lg:rounded-[22px] lg:border
                    [&>button[class*='right-4']]:hidden"
                data-testid="elige-tu-menu">
                {/* El asa */}
                <div className="flex justify-center pt-2 pb-1 touch-none flex-shrink-0" {...asa}>
                    <span className="w-9 h-1 rounded-sm bg-[#3a3a3a]" />
                </div>

                {/* Cabecera */}
                <div className="flex items-end justify-between gap-3 px-5 pt-2.5 flex-shrink-0">
                    <div className="min-w-0">
                        <p className="text-[11px] font-bold tracking-[0.14em] text-brand uppercase truncate" data-testid="menu-objetivo">
                            {nombreHueco} · Objetivo {num1(obj.P)} P · {num1(obj.H)} H · {num1(obj.G)} G
                        </p>
                        <DialogTitle className="font-heading text-[26px] font-bold leading-none text-foreground mt-1">Elige tu menú</DialogTitle>
                        <DialogDescription className="sr-only">Recetas y menús que cuadran con el objetivo de esta comida</DialogDescription>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Cerrar"
                        className="w-9 h-9 rounded-full bg-[#262626] text-foreground flex items-center justify-center flex-shrink-0 hover:bg-[#2f2f2f]">
                        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                </div>

                {/* Los siete filtros, excluyentes, en una fila que se desliza */}
                <div className="mt-3.5 flex-shrink-0">
                    <FilaConFlechas fondo="#161616" etiquetas={{ izq: 'Ver los filtros anteriores', der: 'Ver más filtros' }}
                        className="flex gap-1.5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                        role="radiogroup" aria-label="Filtrar">
                        {FILTROS.map(([k, texto]) => {
                            const on = filtro === k;
                            const n = cuantos(k);
                            return (
                                <button key={k} type="button" role="radio" aria-checked={on} onClick={() => setFiltro(k)}
                                    data-testid={`recetario-momento-${k}`}
                                    className={`flex-none h-[34px] px-3.5 rounded-[17px] border text-sm font-semibold whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                                        on ? 'bg-brand border-brand text-[#140a04]' : 'bg-[#1f1f1f] border-[#2c2c2c] text-[#d9d5d0] hover:border-white/25'}`}>
                                    {texto}
                                    {n !== null && <span className="text-[11px] font-bold opacity-70">{n}</span>}
                                </button>
                            );
                        })}
                    </FilaConFlechas>
                </div>

                {/* Buscador y opciones */}
                <div className="flex gap-2 px-5 pt-2.5 flex-shrink-0">
                    <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-[#8a8580]" />
                        <input type="search" value={textFilter} onChange={(e) => setTextFilter(e.target.value)}
                            placeholder="Buscar receta o alimento" data-testid="recetario-filter"
                            className="w-full h-10 rounded-xl bg-[#1f1f1f] border border-[#2c2c2c] pl-9 pr-3 text-sm text-foreground placeholder:text-[#8a8580] focus:outline-none focus:border-brand/60" />
                    </div>
                    {BIBLIOTECA_DE_CLIENTES && (
                        <button type="button" onClick={() => setOpcionesAbiertas((o) => !o)} aria-expanded={opcionesAbiertas}
                            data-testid="menus-opciones"
                            className={`h-10 px-3 rounded-xl border border-[#2c2c2c] text-sm font-semibold text-foreground flex items-center gap-1.5 flex-shrink-0 ${opcionesAbiertas ? 'bg-[#2c2c2c]' : 'bg-[#1f1f1f]'}`}>
                            <SlidersHorizontal className="w-3.5 h-3.5" /> ±{margenVisto} g
                        </button>
                    )}
                </div>
                {opcionesAbiertas && BIBLIOTECA_DE_CLIENTES && (
                    <div className="mx-5 mt-2 p-3 rounded-xl bg-[#1f1f1f] space-y-3 flex-shrink-0" data-testid="menus-panel-opciones">
                        <div className="flex items-center gap-3">
                            <span className="w-[70px] text-[11px] font-bold tracking-[0.12em] text-[#8a8580]">MARGEN</span>
                            <input type="range" min="1" max="15" step="1" value={margenVisto}
                                onChange={(e) => setMargenVisto(Number(e.target.value))}
                                className="flex-1 accent-orange-500" data-testid="library-margen" aria-label="Margen en gramos" />
                            <span className="font-heading text-base font-bold text-foreground w-12 text-right">±{margenVisto} g</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-[70px] text-[11px] font-bold tracking-[0.12em] text-[#8a8580]">ORIGEN</span>
                            <div className="flex-1 grid grid-cols-2 rounded-[10px] bg-[#141414] p-[3px]" role="radiogroup" aria-label="Origen">
                                {[[true, 'Todos', 'library-fuente-todo'], [false, 'Solo recetario', 'library-fuente-recetario']].map(([v, texto, id]) => (
                                    <button key={id} type="button" role="radio" aria-checked={verDeOtros === v} onClick={() => cambiarVerDeOtros(v)}
                                        data-testid={id}
                                        className={`h-[30px] rounded-lg text-[13px] font-semibold transition-colors ${verDeOtros === v ? 'bg-[#2c2c2c] text-foreground' : 'text-[#8a8580]'}`}>
                                        {texto}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {confirmacion ? (
                    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-6" data-testid="recetario-confirmar-desvio">
                        <p className="font-heading text-[21px] font-bold text-foreground text-center leading-tight mb-1">{confirmacion.menu.nombre}</p>
                        <p className="text-sm text-[#8a8580] text-center mb-4">
                            Ajustada a tus macros, esta receta se te va del objetivo de esta comida:
                        </p>
                        <div className="space-y-1.5 mb-4">
                            {confirmacion.fuera.map(({ m, diff }) => (
                                <p key={m} className={`text-center text-sm font-bold ${diff > 0 ? 'text-[#e8a23a]' : 'text-foreground'}`}>
                                    {diff > 0
                                        ? `Te sobran ${num1(diff)} g de ${MACRO_NOMBRE[m]}`
                                        : `Te faltan ${num1(Math.abs(diff))} g de ${MACRO_NOMBRE[m]}`}
                                </p>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2 rounded-2xl bg-[#1f1f1f] p-3 mb-5 text-center">
                            {[['Tu objetivo', confirmacion.menu.macros_objetivo || obj], ['La receta', confirmacion.menu.macros_totales]].map(([t, m]) => (
                                <div key={t}>
                                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#8a8580] mb-1">{t}</span>
                                    <span className="font-heading text-base font-bold text-foreground">
                                        {['P', 'H', 'G'].map((k) => `${num1(m?.[k])} ${k}`).join(' · ')}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => { setConfirmacion(null); setElegidoId(null); }} data-testid="recetario-elegir-otra"
                                className="flex-1 h-11 rounded-xl bg-[#2c2c2c] text-foreground font-bold text-sm">Elegir otra</button>
                            <button onClick={confirmarReceta} data-testid="recetario-meter-igual"
                                className="flex-1 h-11 rounded-xl bg-brand text-[#140a04] font-bold text-sm">Meterla igual</button>
                        </div>
                        <p className="text-[11px] text-[#8a8580] text-center mt-3">
                            {/* Si lo que falta es un alimento, se dice cuál (MAC-02). */}
                            {algoCon(confirmacion.menu.falta_fuente)
                                ? `Si la metes igual, para cuadrarla le falta ${algoCon(confirmacion.menu.falta_fuente)}.`
                                : 'Si la metes igual, tendrás que ajustar las cantidades a mano.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Resumen */}
                        <p className="px-5 pt-3.5 pb-2 text-[13px] text-[#8a8580] flex-shrink-0" data-testid="menus-recuento">
                            <span className="font-bold text-foreground">{mostrados.length}</span> opciones
                            {` · ${nRecetas} ${nRecetas === 1 ? 'receta' : 'recetas'} · ${nMenus} ${nMenus === 1 ? 'menú' : 'menús'}`}
                            {buscandoMenus ? ' · buscando más…' : ''}
                        </p>

                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-6 space-y-2.5">
                            {error_ && <p className="text-xs text-[#e8a23a] font-medium">{error_}</p>}

                            {recetarioLoading && !(recetario || []).length ? (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <div className="animate-spin rounded-full h-9 w-9 border-4 border-brand border-t-transparent mb-4" />
                                    <p className="text-sm text-[#8a8580]">Cargando los menús…</p>
                                </div>
                            ) : mostrados.length === 0 && !buscandoMenus ? (
                                <p className="text-sm text-[#8a8580] text-center py-10"
                                    data-testid={actual?.sinCosechar ? 'biblioteca-sin-cosechar' : 'menus-vacio'}>
                                    Nada encaja con estos filtros. Prueba a ampliar el margen.
                                </p>
                            ) : (
                                mostrados.map((fila) => {
                                    if (fila.tipo === 'receta') {
                                        const r = fila.dato;
                                        const id = `r-${r.id}`;
                                        return (
                                            <TarjetaMenu key={id}
                                                kicker={`Receta${(r.momentos || []).length ? ` · ${r.momentos.map((m) => MOMENTO_MAYUS[m] || m).join(' / ')}` : ''}`}
                                                origen="Recetario 12en12"
                                                titulo={r.nombre}
                                                macros={null}
                                                esMenu={false}
                                                ingredientes={(r.alimentos || []).map((n) => ({ q: '·', n }))}
                                                // «Se ajusta», no «se cuadra» (MAC-02, 14-10): lo que le falte se dice al elegirla.
                                                social={r.completa === false ? 'Complemento: no cubre la proteína' : 'Se ajusta a tus macros'}
                                                textoBoton="Elegir y ajustar"
                                                elegido={elegidoId === id}
                                                ocupado={aplicandoId === r.id}
                                                deshabilitado={!!aplicandoId || applying}
                                                onElegir={() => aplicarReceta(r)}
                                                testId={`recetario-menu-${fila.i}`} />
                                        );
                                    }
                                    const m = fila.dato;
                                    const id = `m-${m.biblioteca_id}`;
                                    const mm = m.macros_metodo || m.macros_totales || {};
                                    const momentos = (m.momentos || []).filter((x) => MOMENTO_MAYUS[x]);
                                    return (
                                        <TarjetaMenu key={id}
                                            kicker={`Menú${momentos.length ? ` · ${momentos.map((x) => MOMENTO_MAYUS[x]).join(' / ')}` : ''}`}
                                            origen={etiquetaAutor(m)}
                                            origenNaranja={!!m.de_entrenador}
                                            titulo={tituloDelMenu(m)}
                                            macros={['P', 'H', 'G'].map((k) => ({ k, v: mm[k] || 0, d: (mm[k] || 0) - (obj[k] || 0) }))}
                                            esMenu
                                            ingredientes={(m.items || []).map((it) => ({ q: it.cantidad_display, n: it.nombre }))}
                                            social={m.personas > 1 ? `Lo usan ${m.personas} personas`
                                                : m.personas === 1 ? 'Lo usa 1 persona'
                                                : m.de_jesus ? 'De los menús de Jesús' : ''}
                                            estado={m.clavado ? 'Clavado' : m.cuadrada ? 'Cuadra' : null}
                                            textoBoton={m.ajustado ? 'Añadir ajustado a ti' : 'Elegir tal cual'}
                                            elegido={elegidoId === id}
                                            ocupado={applying && elegidoId === id}
                                            deshabilitado={applying || !!aplicandoId}
                                            onElegir={() => aplicar(m)}
                                            testId={`library-menu-${fila.i}`} />
                                    );
                                })
                            )}

                            {buscandoMenus && mostrados.length > 0 && (
                                <div className="flex items-center justify-center gap-2 py-4 text-[#8a8580]">
                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" />
                                    <span className="text-xs">Buscando más menús que te cuadren…</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default LibraryMenusModal;
